/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type {
  IpAutoLoginError,
  IpAutoLoginErrorCode,
  IpAutoLoginStatus,
  SignInErrorPayload
} from "../signIn.types";
import {
  ERROR_CODE_BIND_FAILED,
  ERROR_CODE_DOMAIN_TOKEN_FAILED,
  ERROR_CODE_PROVISION_FAILED,
  ERROR_CODE_UNKNOWN,
  IP_AUTOLOGIN_EMPTY_CREDENTIAL_MESSAGE,
  IP_AUTO_LOGIN_STATUS_BINDING,
  IP_AUTO_LOGIN_STATUS_FETCHING_DOMAIN_TOKEN,
  IP_AUTO_LOGIN_STATUS_FETCHING_IP_JWT,
  IP_AUTO_LOGIN_STATUS_PROVISIONING,
  IPL_ACCOUNT_NOT_FOUND,
  IPL_BAD_REQUEST,
  IPL_CANCELLED,
  IPL_CONFLICT,
  IPL_EMPTY_CREDENTIAL,
  IPL_INTERNAL_SERVER_ERROR,
  IPL_NETWORK_ERROR,
  IPL_NOT_ELIGIBLE,
  IPL_TIMEOUT
} from "../signIn.types";
import {
  extractServiceErrorCode,
  extractServiceErrorMessage,
  localizedErrorFallback
} from "../../../model/utility";
import { LocaleKeys } from "../../../model/locale";
import {
  ERROR_FRAGMENT_ABORT,
  ERROR_FRAGMENT_CANCEL,
  ERROR_FRAGMENT_ERR_NETWORK,
  ERROR_FRAGMENT_NETWORK_ERROR,
  ERROR_FRAGMENT_TIMEOUT_LOWER,
  ERROR_FRAGMENT_TIMEOUT_UPPER,
  HTTP_STATUS_BAD_REQUEST,
  HTTP_STATUS_CONFLICT,
  HTTP_STATUS_FORBIDDEN,
  HTTP_STATUS_NOT_FOUND,
  HTTP_STATUS_PRECONDITION_FAILED,
  HTTP_STATUS_SERVER_ERROR_MIN,
  HTTP_STATUS_UNAUTHORIZED
} from "../signIn.constants";

/**
 * The catalog's designated catch-all for a sign-in failure we cannot map:
 * `A010` — "Global sign-in unexpected error" (SIGN_IN, P2).
 *
 * Deliberately generic. IMPLEMENTATION_GUIDANCE.md §6: "Do not emit a code for
 * a condition it does not describe… emitting a near-enough one is worse than
 * emitting a generic one, because it looks precise."
 *
 * A literal rather than an `ErrorTypes` member because the lib has no constant
 * for it: `ErrorTypes` starts at `A011`, since `A010` is raised by unity-web
 * itself for conditions no service error describes and never by a
 * `*ServiceError` class. Everything the lib *does* assign is referenced through
 * `ErrorTypes` — see `signInErrorActions`.
 */
export const SIGN_IN_FALLBACK_ERROR_CODE: string = "A010";

/**
 * A catalog sign-in code: the letter `A` followed by exactly three digits.
 *
 * The prefix is load-bearing — the telemetry pipeline derives `error_class`
 * from it and the client never sends that field, so a value with no prefix
 * lands in no class at all.
 */
const CATALOG_CODE_PATTERN: RegExp = /^A\d{3}$/;

/**
 * Numbers unity-web still emits that the catalog has already given to a
 * different operation. Reported as {@link SIGN_IN_FALLBACK_ERROR_CODE} until
 * the real families are allocated upstream (UAW-2416 remediation A1/A2).
 *
 * `A702` is the reason this exists: it is an **active P1** code meaning
 * "Connected Query Failure", with operator KB articles and trained ITSI
 * thresholds, and `/bind` was emitting it for an unknown-error. A support
 * agent looking it up got a confidently wrong answer.
 *
 * IMPLEMENTATION_GUIDANCE.md §6 — "emitting a near-enough one is worse than
 * emitting a generic one, because it looks precise." Granularity comes back
 * the moment the codes are real; until then the specific cause stays visible
 * in the `error_type` and `http_status` diagnostic fields.
 *
 * Deliberately excludes the *unregistered* families (claim's A900-A911, the
 * TSRI and MSO blocks). Those resolve to nothing rather than to something
 * else, and the plan is to register them as-is — suppressing them would make
 * telemetry worse, not better.
 */
const COLLIDING_CODES: string[] = [
  // /bind → catalog: BASE_CONNECTIVITY / FREQUENT_WHISPERS / WHISPERS
  "A700",
  "A701",
  "A702",
  "A703",
  "A704",
  "A705",
  "A706",
  "A707",
  "A708",
  "A709",
  // /v1/ip-autologin/token → catalog: GET /v1/softClientCert (deprecated)
  "A500",
  "A501",
  "A502",
  "A503",
  "A504",
  "A505",
  "A506",
  "A507",
  "A508",
  "A509",
  "A510",
  "A511",
  "A512",
  "A513",
  "A514",
  "A515",
  // feProvisioning → catalog: /connectTime + mRPC frontendAuthenticate
  "A600",
  "A601",
  "A602",
  "A603",
  "A604",
  "A605",
  "A606",
  "A607",
  "A608",
  "A609",
  "A610",
  "A611"
];

/**
 * Returns the catalog error code carried by a thrown sign-in error, or
 * {@link SIGN_IN_FALLBACK_ERROR_CODE} when there isn't one.
 *
 * The result is **validated, not trusted**. `AppError.code` is not always a
 * catalog code — `UnauthorisedError` and `UnauthenticatedError` construct with
 * the human strings "Not Authorised" / "Not Authenticated" — and an axios error
 * carries transport codes like `ERR_NETWORK`. Anything that is not `A` plus
 * three digits becomes `A010` rather than reaching telemetry as-is.
 *
 * Note `AppError` implements `IErrorBase` and does **not** extend `Error`, so
 * `instanceof Error` is false for every mapped service error. Detection is by
 * the `getCode` accessor, which is the interface's contract.
 */
export function extractSignInErrorCode(error: unknown): string {
  if (error === null || error === undefined || typeof error !== "object") {
    return SIGN_IN_FALLBACK_ERROR_CODE;
  }
  const candidate = error as { getCode?: () => string };
  if (typeof candidate.getCode !== "function") {
    return SIGN_IN_FALLBACK_ERROR_CODE;
  }
  const code: string = candidate.getCode();
  if (typeof code !== "string" || !CATALOG_CODE_PATTERN.test(code)) {
    return SIGN_IN_FALLBACK_ERROR_CODE;
  }
  if (COLLIDING_CODES.includes(code)) {
    return SIGN_IN_FALLBACK_ERROR_CODE;
  }
  return code;
}

/**
 * Normalises anything thrown out of a sign-in thunk into the payload the view
 * needs: a catalog code plus user-facing copy.
 *
 * `fallbackMessage` is used when the error carries no usable text — it should
 * already be localised, since the view renders it verbatim.
 */
export function toSignInErrorPayload(
  error: unknown,
  fallbackMessage: string
): SignInErrorPayload {
  const serviceCode: string | undefined = extractServiceErrorCode(error);
  return {
    code: extractSignInErrorCode(error),
    message: extractServiceErrorMessage(error, fallbackMessage),
    serviceCode: serviceCode !== undefined ? serviceCode : ""
  };
}

/**
 * Returns the HTTP status code from an Axios-style error, or null.
 */
export function extractHttpStatus(error: unknown): number | null {
  if (error === null || error === undefined || typeof error !== "object") {
    return null;
  }
  const errorRecord = error as Record<string, unknown>;
  const response = errorRecord.response as Record<string, unknown> | undefined;
  if (response === null || response === undefined) {
    return null;
  }
  const status = Number(response.status);
  if (Number.isNaN(status)) {
    return null;
  }
  return status;
}

/**
 * Maps a step 3 (ipAutologinToken) failure to an error code. Every step 3
 * failure is fallback-eligible — the service simply isn't usable, so the
 * orchestrator must move on to the next auth type.
 */
export function classifyIpJwtError(
  httpStatus: number | null,
  message: string,
  watchdogFired: boolean
): IpAutoLoginErrorCode {
  if (message === IP_AUTOLOGIN_EMPTY_CREDENTIAL_MESSAGE) {
    return IPL_EMPTY_CREDENTIAL;
  }
  if (httpStatus === HTTP_STATUS_BAD_REQUEST) {
    return IPL_BAD_REQUEST;
  }
  if (httpStatus === HTTP_STATUS_NOT_FOUND) {
    return IPL_ACCOUNT_NOT_FOUND;
  }
  if (httpStatus === HTTP_STATUS_CONFLICT) {
    return IPL_CONFLICT;
  }
  // 401 (authentication failed) and 403 (not from a recognised gateway) both
  // mean this device cannot use IP auto-login here — the same outcome as 412.
  if (
    httpStatus === HTTP_STATUS_PRECONDITION_FAILED ||
    httpStatus === HTTP_STATUS_UNAUTHORIZED ||
    httpStatus === HTTP_STATUS_FORBIDDEN
  ) {
    return IPL_NOT_ELIGIBLE;
  }
  if (httpStatus !== null && httpStatus >= HTTP_STATUS_SERVER_ERROR_MIN) {
    return IPL_INTERNAL_SERVER_ERROR;
  }
  if (
    message.includes(ERROR_FRAGMENT_TIMEOUT_LOWER) ||
    message.includes(ERROR_FRAGMENT_TIMEOUT_UPPER)
  ) {
    return IPL_TIMEOUT;
  }
  if (
    message.includes(ERROR_FRAGMENT_ABORT) ||
    message.includes(ERROR_FRAGMENT_CANCEL)
  ) {
    // The total-timeout watchdog aborts the in-flight request (or its
    // retry backoff), which surfaces as "Request aborted". That is a
    // timeout, not a caller-initiated cancellation.
    return watchdogFired ? IPL_TIMEOUT : IPL_CANCELLED;
  }
  if (
    message.includes(ERROR_FRAGMENT_NETWORK_ERROR) ||
    message.includes(ERROR_FRAGMENT_ERR_NETWORK)
  ) {
    return IPL_NETWORK_ERROR;
  }
  return ERROR_CODE_UNKNOWN;
}

/**
 * Classifies an IP auto-login error from the HTTP status and message.
 *
 * Produces both halves of {@link IpAutoLoginError}: the catalog `code` that
 * reaches telemetry and the UI, and the finer-grained `reason` that drives
 * orchestration. The code is read off the thrown error rather than re-derived,
 * so a step with a real catalog family reports its precise cause — the
 * `/domainToken` and `/provisionDevices` steps now carry `A43x` / `A46x`
 * instead of one bucket per step.
 */
export function classifyIpAutoLoginError(
  error: unknown,
  step: IpAutoLoginStatus,
  watchdogFired: boolean = false
): IpAutoLoginError {
  const httpStatus: number | null = extractHttpStatus(error);
  // What the classifier matches its fragments against — the transport text,
  // which is where "timeout" / "Request aborted" / "Network Error" appear.
  const transportMessage: string =
    error instanceof Error ? error.message : String(error);
  // What the user reads. `String(error)` is "[object Object]" for every mapped
  // service error (AppError does not extend Error), and the axios summary for
  // an unwrapped one, so neither is fit to show on its own.
  const message: string = extractServiceErrorMessage(error, transportMessage);
  const code: string = extractSignInErrorCode(error);

  if (step === IP_AUTO_LOGIN_STATUS_FETCHING_IP_JWT) {
    return {
      code,
      reason: classifyIpJwtError(httpStatus, transportMessage, watchdogFired),
      message,
      fallbackEligible: true
    };
  }

  // Steps 4-6: bind, domain-token, provision. IP auto-login is silent — the
  // user is on the splash screen and never asked for it — so no failure of it
  // is shown. Every step falls back to the next auth type, which can surface
  // its own error if the cause is shared (a suspended account, say).
  if (step === IP_AUTO_LOGIN_STATUS_BINDING) {
    return {
      code,
      reason: ERROR_CODE_BIND_FAILED,
      message,
      fallbackEligible: true
    };
  }
  if (step === IP_AUTO_LOGIN_STATUS_FETCHING_DOMAIN_TOKEN) {
    return {
      code,
      reason: ERROR_CODE_DOMAIN_TOKEN_FAILED,
      message,
      fallbackEligible: true
    };
  }
  if (step === IP_AUTO_LOGIN_STATUS_PROVISIONING) {
    return {
      code,
      reason: ERROR_CODE_PROVISION_FAILED,
      message,
      fallbackEligible: true
    };
  }
  return {
    code,
    reason: ERROR_CODE_UNKNOWN,
    message,
    fallbackEligible: true
  };
}

/**
 * Normalises anything thrown out of the IP auto-login sequence into an
 * IpAutoLoginError. An object that a step handler already classified — it
 * carries a `reason` — is passed through unchanged.
 */
export function toIpAutoLoginError(error: unknown): IpAutoLoginError {
  if (
    error !== null &&
    error !== undefined &&
    typeof error === "object" &&
    "reason" in error
  ) {
    return error as IpAutoLoginError;
  }
  return {
    code: extractSignInErrorCode(error),
    reason: ERROR_CODE_UNKNOWN,
    message: extractServiceErrorMessage(
      error,
      localizedErrorFallback(LocaleKeys.SIGN_IN_AUTO_LOGIN_ERROR_MESSAGE)
    ),
    fallbackEligible: true
  };
}
